import type { components } from '@/lib/api/generated/schema'
import { ApiError } from '@/lib/api/errors'
import {
  api,
  normalizePrivateProfile,
  normalizeProfileSubset,
} from '@/lib/api'
import type { TokenPair } from '@/lib/api/types'
import { refreshTokenStore } from '@/lib/auth/token-store'
import { env, type AuthMode } from '@/lib/config/env'

type Schemas = components['schemas']

export type AppConfig = Schemas['AppConfig']
export type PrivateProfile = Schemas['PrivateProfile']
export type LegalStatus = Schemas['LegalStatusResponse']
export type LoginRequest = Schemas['LoginRequest']
export type RegisterRequest = Schemas['RegisterRequest']
export type RegisterResponse = Schemas['RegisterResponse']
export type GoogleRegisterRequest = Schemas['GoogleRegisterRequest']
export type GoogleOAuthRequest = Schemas['GoogleOAuthRequest']
export type GoogleRegisterResponse =
  | TokenPair
  | Schemas['RegistrationRequired']
export type ForgotPasswordResponse = Schemas['ForgotPasswordResponse']
export type TokenPairResponse = TokenPair

const publicRequest = { skipAuth: true, skipRefresh: true } as const

/**
 * Web sends no refresh token in the body (the HttpOnly cookie does the work);
 * native sends the secure-store token (`RefreshRequest`).
 */
export function refreshRequestBody(
  mode: AuthMode,
  refreshToken: string | null,
): { refreshToken?: string } {
  if (mode !== 'bearer' || !refreshToken) return {}
  return { refreshToken }
}

/**
 * Thin feature boundary over the frozen `/api/v1` contract.
 *
 * Requests go through the shared typed `ApiClient` (cookie mode + CSRF on web,
 * bearer mode on native) and return generated OpenAPI types. No response is
 * cached here: the backend owns server state (D-042).
 */
export const authApi = {
  getAppConfig(): Promise<AppConfig> {
    return api.get<AppConfig>('/app-config', publicRequest)
  },

  login(body: LoginRequest): Promise<TokenPairResponse> {
    return api.post<TokenPairResponse>('/auth/login', body, publicRequest)
  },

  register(body: RegisterRequest): Promise<RegisterResponse> {
    return api.post<RegisterResponse>('/auth/register', body, publicRequest)
  },

  verifyEmail(token: string): Promise<{ ok: true }> {
    return api.post<{ ok: true }>('/auth/verify-email', { token }, publicRequest)
  },

  resendVerificationEmail(email: string): Promise<ForgotPasswordResponse> {
    return api.post<ForgotPasswordResponse>(
      '/auth/resend-verification-email',
      { email },
      publicRequest,
    )
  },

  forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    return api.post<ForgotPasswordResponse>(
      '/auth/forgot-password',
      { email },
      publicRequest,
    )
  },

  resetPassword(token: string, password: string): Promise<{ ok: true }> {
    return api.post<{ ok: true }>(
      '/auth/reset-password',
      { token, password },
      publicRequest,
    )
  },

  /**
   * Restores a session. Web sends the HttpOnly refresh cookie (and the CSRF
   * header); native sends the secure-store refresh token in the JSON body.
   */
  refresh(): Promise<TokenPairResponse> {
    const body = refreshRequestBody(env.authMode, refreshTokenStore.get())
    if (env.authMode === 'bearer' && !body.refreshToken) {
      return Promise.reject(
        new ApiError('UNAUTHORIZED', 'No refresh token available', {
          status: 401,
        }),
      )
    }
    return api.post<TokenPairResponse>('/auth/refresh', body, publicRequest)
  },

  logout(): Promise<{ ok: true }> {
    const refreshToken = refreshTokenStore.get()
    return api.post<{ ok: true }>(
      '/auth/logout',
      env.authMode === 'bearer' && refreshToken ? { refreshToken } : {},
      { skipAuth: true, skipRefresh: true },
    )
  },

  getMe(): Promise<PrivateProfile> {
    return api.get<PrivateProfile>('/me').then(normalizePrivateProfile)
  },

  updateProfile(
    body: Schemas['UpdateProfileRequest'],
  ): Promise<Schemas['ProfileSubset']> {
    return api
      .patch<Schemas['ProfileSubset']>('/me', body)
      .then(normalizeProfileSubset)
  },

  legalStatus(): Promise<LegalStatus> {
    return api.post<LegalStatus>('/auth/legal-status', {})
  },

  acceptTerms(termsVersion: string): Promise<Schemas['AcceptTermsResponse']> {
    return api.post<Schemas['AcceptTermsResponse']>('/auth/accept-terms', {
      termsVersion,
      accepted: true,
    })
  },

  googleLogin(idToken: string): Promise<GoogleRegisterResponse> {
    return api.post<GoogleRegisterResponse>(
      '/auth/oauth/google',
      { idToken },
      publicRequest,
    )
  },

  googleRegister(
    body: GoogleRegisterRequest,
  ): Promise<TokenPairResponse> {
    return api.post<TokenPairResponse>(
      '/auth/oauth/google/register',
      body,
      publicRequest,
    )
  },
}

export function isRegistrationRequired(
  response: GoogleRegisterResponse,
): response is Schemas['RegistrationRequired'] {
  return 'registrationRequired' in response
}
